import { useCallback, useMemo, useState } from "react";

import type { Id } from "../../api/types";
import { defaultExpanded, withAncestorsOpen, type NavModel } from "./model/nav";

/** What is open: the person's own choices, or the first view of the tree until they make one. */
export function useOpenSet(model: NavModel, selected: Id | null) {
  const [chosen, setChosen] = useState<Set<Id> | null>(null);
  const [openedFor, setOpenedFor] = useState<Id | null>(null);
  const first = useMemo(() => defaultExpanded(model), [model]);

  // A node named by the address (a link from what's new) is made visible once.
  if (selected !== openedFor && (selected === null || model.byId.has(selected))) {
    setOpenedFor(selected);
    if (selected) setChosen(withAncestorsOpen(model, chosen ?? first, selected));
  }

  const expanded = chosen ?? first;
  const toggle = useCallback(
    (id: Id) => {
      setChosen((before) => {
        const next = new Set(before ?? first);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [first],
  );
  const open = useCallback(
    (ids: ReadonlySet<Id>) => {
      if (ids.size > 0) setChosen((before) => new Set([...(before ?? first), ...ids]));
    },
    [first],
  );
  return { expanded, toggle, open };
}
